/**
 * AP-23 (Phase B + C) — Listen-Layouts serverseitig.
 *
 * Spaltenauswahl, Filterfelder, Sortierung und Seitengröße folgen dem Benutzer
 * statt dem Gerät. Der `localStorage` bleibt — aber als **Cache**, nicht als
 * Wahrheit:
 *
 *   1. Nach dem Login einmal `GET /users/me/ui-settings`, Ergebnis (aufgelöst:
 *      eigener Wert, sonst Mandanten-Standard) in den Cache spiegeln.
 *   2. Die Listen lesen weiterhin **synchron** aus dem Cache — sonst zeigte jede
 *      Liste erst ihr Default-Layout und spränge dann auf das gespeicherte; genau
 *      der Flash, der an anderer Stelle (Theme-Snapshot, PWA-Preloader) mühsam
 *      beseitigt wurde. Nebeneffekt: offline funktioniert alles unverändert.
 *   3. Änderungen: sofort in den Cache, **debounced** an die API.
 *
 * Diese Datei liegt bewusst in `src/lib/` und nicht unter `features/`: Jede
 * Listenseite braucht sie, ein Feature-Import erzeugte sofort neue
 * Modulgrenzen-Verstöße (dieselbe Begründung wie in AP-21-B).
 *
 * **Cache-Schlüssel enthalten die `userId`.** Die alten Schlüssel
 * (`columnbar_layout_summary`) waren Gerät-Schlüssel: Zwei Benutzer an einem
 * Rechner teilten sich das Layout. Solange alles lokal war, war das nur
 * unschön — mit einer Server-Wahrheit würde daraus ein Fehler, weil der zweite
 * Benutzer die Einrichtung des ersten in sein Konto hochschöbe.
 */

import { api, getAccessToken } from './axios'

// ─── Schlüssel & Konstanten ──────────────────────────────────────────────────

/** Cache-Präfix; der volle Schlüssel lautet `symphenia.ui.<userId>.<key>`. */
const CACHE_PREFIX = 'symphenia.ui'

/** Zuletzt getippte Filterwerte — bleiben absichtlich lokal (Arbeitskontext, kein Setup). */
const LAST_VALUES_PREFIX = 'symphenia.ui.lastvalues'

/**
 * Mandanten-Standard je Schlüssel (AP-23 Phase D). Wird mitgespiegelt, damit die
 * Listen synchron beim Aufbau wissen, ob das eigene Layout abweicht — ohne
 * Nachladen und ohne Nachspringen der Oberfläche.
 */
const TENANT_DEFAULT_PREFIX = 'symphenia.ui.tenantdefault'

/** Merker der einmaligen Übernahme alter, gerätegebundener Werte (Phase C). */
const MIGRATED_KEY = 'symphenia.uiSettings.migrated'

/**
 * Wartezeit vor dem Schreiben an die API. Die FilterBar meldet jede
 * Wertänderung; ohne Debounce wäre das ein Request je Tastendruck.
 */
const DEBOUNCE_MS = 800

/** Die elf gerätegebundenen Schlüssel aus der Zeit vor AP-23 (Phase C). */
const LEGACY_KEYS = [
  'columnbar_layout_summary',
  'columnbar_layout_payroll',
  'columnbar_layout_absences',
  'columnbar_layout_projects',
  'filterbar_layout_summary',
  'filterbar_layout_payroll',
  'filterbar_layout_absences',
  'filterbar_layout_projects',
  'pagesize_summary',
  'pagesize_payroll',
  'pagesize_absences',
] as const

// ─── Modulzustand ────────────────────────────────────────────────────────────

let currentUserId: string | null = null

/**
 * Darf der angemeldete Benutzer Mandanten-Standards setzen (ADMIN)?
 *
 * Liegt hier und nicht im `auth.store`, weil die Standard-Aktionen in einer
 * geteilten Komponente sitzen: `src/components/` darf nicht in ein Feature
 * hineingreifen (AP-21-B). Der Store meldet die Rolle beim Anmelden hierher —
 * dieselbe Richtung wie die `userId` und damit keine neue Modulgrenze.
 */
let tenantAdmin = false

/** Ausstehende, noch nicht gesendete Änderungen je Schlüssel. */
const pending = new Map<string, unknown>()
let debounceTimer: ReturnType<typeof setTimeout> | null = null

/** Zuletzt erfolgreich gesendeter Stand je Schlüssel — verhindert Leerlauf-Requests. */
const lastSent = new Map<string, string>()

/** Zuletzt über `saveUiSettingIfChanged` gesehener Stand je Schlüssel. */
const lastLocal = new Map<string, string>()

let flushListenerAttached = false

// ─── Interne Helfer ──────────────────────────────────────────────────────────

function cacheKey(key: string): string {
  return `${CACHE_PREFIX}.${currentUserId ?? 'anon'}.${key}`
}

function tenantDefaultCacheKey(key: string): string {
  return `${TENANT_DEFAULT_PREFIX}.${currentUserId ?? 'anon'}.${key}`
}

/** Entfernt alle Einträge unter `prefix`, deren Schlüssel der Server nicht mehr kennt. */
function dropStaleKeys(prefix: string, keep: Set<string>): void {
  try {
    const stale: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const full = localStorage.key(i)
      if (full?.startsWith(prefix) && !keep.has(full.slice(prefix.length))) stale.push(full)
    }
    for (const full of stale) localStorage.removeItem(full)
  } catch {
    // ignorieren
  }
}

function readJson<T>(storageKey: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw == null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function writeJson(storageKey: string, value: unknown): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value))
  } catch {
    // Speicher voll oder gesperrt (privates Fenster) — der Server hat den Wert.
  }
}

/**
 * Beim Verlassen der Seite bleibt für den Debounce keine Zeit mehr. `fetch` mit
 * `keepalive` überlebt den Seitenwechsel; `sendBeacon` scheidet aus, weil es
 * keinen Authorization-Header setzen kann und das Token nur im Speicher liegt.
 */
function attachFlushListener(): void {
  if (flushListenerAttached || typeof window === 'undefined') return
  flushListenerAttached = true
  const handler = () => {
    if (document.visibilityState === 'hidden') flushUiSettings()
  }
  document.addEventListener('visibilitychange', handler)
  window.addEventListener('pagehide', () => flushUiSettings())
}

function sendKeepalive(key: string, value: unknown): void {
  const token = getAccessToken()
  if (!token) return
  try {
    void fetch(`${api.defaults.baseURL}/users/me/ui-settings/${key}`, {
      method: 'PUT',
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ value }),
    })
  } catch {
    // Der Wert steht im Cache; die nächste Änderung schickt ihn erneut.
  }
}

async function pushSetting(key: string, value: unknown): Promise<void> {
  try {
    await api.put(`/users/me/ui-settings/${key}`, { value })
    lastSent.set(key, JSON.stringify(value))
  } catch {
    // Offline oder Server nicht erreichbar: bewusst kein Wiederholungslauf.
    // Der Cache trägt den Wert weiter, die nächste Änderung sendet ihn erneut.
  }
}

// ─── Öffentliche API ─────────────────────────────────────────────────────────

/**
 * Bindet den Cache an einen Benutzer. Beim Abmelden mit `null` aufrufen: Der
 * Cache selbst bleibt stehen (er ist benutzergebunden und beim nächsten
 * Anmelden sofort wieder gültig), aber offene Sendungen dürfen nicht in die
 * Sitzung des nächsten Benutzers hineinlaufen.
 */
export function setUiSettingsUser(userId: string | null, isTenantAdmin = false): void {
  tenantAdmin = userId ? isTenantAdmin : false
  if (userId === currentUserId) return
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  pending.clear()
  lastSent.clear()
  lastLocal.clear()
  currentUserId = userId
}

/** Darf dieser Benutzer Mandanten-Standards setzen und entfernen? */
export function canManageTenantDefaults(): boolean {
  return tenantAdmin
}

/** Synchroner Lesezugriff für den Aufbau einer Liste. */
export function loadUiSetting<T>(key: string, fallback: T): T {
  return readJson<T>(cacheKey(key), fallback)
}

/** Sofort in den Cache, debounced an die API. */
export function saveUiSetting(key: string, value: unknown): void {
  writeJson(cacheKey(key), value)

  // Unverändert? Dann nicht senden — die FilterBar meldet auch Änderungen, die
  // das Layout gar nicht betreffen (z. B. getippte Werte).
  if (lastSent.get(key) === JSON.stringify(value)) return

  attachFlushListener()
  pending.set(key, value)
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    debounceTimer = null
    const batch = Array.from(pending.entries())
    pending.clear()
    for (const [k, v] of batch) void pushSetting(k, v)
  }, DEBOUNCE_MS)
}

/**
 * Wie `saveUiSetting`, schreibt aber nur, wenn sich der Wert seit dem letzten
 * Aufruf für diesen Schlüssel tatsächlich geändert hat.
 *
 * Gedacht für die Sync-Effekte der Listenseiten, die beim Mount mitlaufen. Ohne
 * diese Sperre legte schon das **Öffnen** einer Liste eine eigene Einstellung an
 * — und ein Mandanten-Standard griffe für diesen Benutzer danach nie wieder,
 * weil eine eigene Einstellung immer gewinnt. (Beim Prüfen im Browser genau so
 * beobachtet: ein Aufruf der Zusammenfassung erzeugte zwei Datensätze, ohne dass
 * jemand etwas verstellt hatte.)
 */
export function saveUiSettingIfChanged(key: string, value: unknown): void {
  const serialized = JSON.stringify(value)
  if (lastLocal.get(key) === serialized) return
  const first = !lastLocal.has(key)
  lastLocal.set(key, serialized)
  // Der erste Aufruf ist der Mount mit dem geladenen Wert — nichts zu tun.
  if (first) return
  saveUiSetting(key, value)
}

/**
 * Meldet der Bibliothek, welchen Wert eine Liste gerade **angewendet** hat, ohne
 * ihn zu speichern.
 *
 * Gebraucht nach dem Zurücksetzen: Der Sync-Effekt der Liste läuft gleich danach
 * los und würde den frisch gesetzten Standard sonst umgehend wieder als eigene
 * Einstellung hochschieben — das Zurücksetzen wäre wirkungslos. Umgekehrt darf
 * `lastLocal` nicht einfach leer bleiben, sonst verschluckte die
 * Mount-Erkennung in `saveUiSettingIfChanged` die nächste echte Änderung.
 */
export function markUiSettingApplied(key: string, value: unknown): void {
  lastLocal.set(key, JSON.stringify(value))
}

/** Ausstehende Änderungen sofort senden (Seitenwechsel, Unmount, Tab verlassen). */
export function flushUiSettings(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  if (pending.size === 0) return
  const batch = Array.from(pending.entries())
  pending.clear()
  const leaving = typeof document !== 'undefined' && document.visibilityState === 'hidden'
  for (const [k, v] of batch) {
    if (leaving) sendKeepalive(k, v)
    else void pushSetting(k, v)
  }
}

/**
 * Eigene Einstellung zurücksetzen — danach greift der Mandanten-Standard bzw. der
 * Code-Default.
 *
 * Zurückgegeben wird der Wert, der ab jetzt gilt: der Mandanten-Standard, sonst
 * `null` für „nimm deinen Code-Default". Die aufrufende Liste wendet ihn direkt
 * an, statt auf einen erneuten Abgleich zu warten.
 */
export function resetUiSetting<T = unknown>(key: string): T | null {
  pending.delete(key)
  lastSent.delete(key)
  lastLocal.delete(key)
  try {
    localStorage.removeItem(cacheKey(key))
  } catch {
    // ignorieren
  }
  void api.delete(`/users/me/ui-settings/${key}`).catch(() => {
    // Offline: Der Cache ist bereits leer, der Server folgt beim nächsten Versuch.
  })
  return loadTenantDefault<T>(key)
}

/** Hat dieser Benutzer auf diesem Gerät schon einen Cache? (Erstbesuch-Erkennung) */
export function hasUiSettingsCache(userId: string): boolean {
  try {
    const prefix = `${CACHE_PREFIX}.${userId}.`
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(prefix)) return true
    }
  } catch {
    // ignorieren
  }
  return false
}

// ─── Mandanten-Standards (AP-23 Phase D) ─────────────────────────────────────

/**
 * Der Mandanten-Standard für einen Schlüssel, sofern einer gesetzt ist.
 *
 * Er ist ein **Startwert, kein Zwang**: Er greift nur für Benutzer ohne eigene
 * Einstellung, und jeder darf jederzeit abweichen.
 */
export function loadTenantDefault<T>(key: string, fallback: T | null = null): T | null {
  return readJson<T | null>(tenantDefaultCacheKey(key), fallback)
}

/** Weicht der aktuelle Stand vom Mandanten-Standard ab? (false, wenn keiner gesetzt ist) */
export function differsFromTenantDefault(key: string, value: unknown): boolean {
  const std = loadTenantDefault<unknown>(key)
  if (std == null) return false
  return JSON.stringify(std) !== JSON.stringify(value)
}

export interface TenantUiDefault {
  key: string
  value: unknown
  updatedBy: string | null
  updatedByName: string | null
  updatedAt: string
}

/** Alle gesetzten Mandanten-Standards — für die Pflegeoberfläche (ADMIN). */
export async function fetchTenantDefaults(): Promise<TenantUiDefault[]> {
  const { data } = await api.get<TenantUiDefault[]>('/tenant/ui-settings')
  return data
}

/**
 * Aktuelles Layout als Mandanten-Standard setzen (ADMIN).
 *
 * Wirkt nur auf Benutzer **ohne** eigene Einstellung — eine Admin-Änderung darf
 * fremde Einrichtungen nicht stillschweigend überschreiben.
 */
export async function saveTenantDefault(key: string, value: unknown): Promise<void> {
  await api.put(`/tenant/ui-settings/${key}`, { value })
  writeJson(tenantDefaultCacheKey(key), value)
}

export async function deleteTenantDefault(key: string): Promise<void> {
  await api.delete(`/tenant/ui-settings/${key}`)
  try {
    localStorage.removeItem(tenantDefaultCacheKey(key))
  } catch {
    // ignorieren
  }
}

// ─── Filterwerte (bleiben lokal) ─────────────────────────────────────────────

/**
 * `lastValues` werden bewusst **nicht** synchronisiert: Ein Datumsbereich vom
 * Vormittag am Desktop gehört nicht auf den Laptop gespiegelt. Sie hängen
 * trotzdem am Benutzer, damit sie an einem geteilten Rechner nicht sichtbar
 * werden.
 */
export function loadFilterLastValues<T>(layoutKey: string, fallback: T): T {
  return readJson<T>(`${LAST_VALUES_PREFIX}.${currentUserId ?? 'anon'}.${layoutKey}`, fallback)
}

export function saveFilterLastValues(layoutKey: string, values: unknown): void {
  writeJson(`${LAST_VALUES_PREFIX}.${currentUserId ?? 'anon'}.${layoutKey}`, values)
}

// ─── Abgleich mit dem Server (nach dem Login) ────────────────────────────────

interface ResolvedUiSetting {
  value: unknown | null
  tenantDefault: unknown | null
}

/**
 * Holt alle Einstellungen des Benutzers und spiegelt sie in den Cache.
 *
 * Schlägt der Aufruf fehl (offline, Backend älter als das Frontend), bleibt der
 * Cache unverändert stehen — die Listen zeigen dann das zuletzt Bekannte statt
 * auf die Code-Defaults zurückzufallen.
 */
export async function syncUiSettingsFromServer(userId: string, isTenantAdmin = false): Promise<void> {
  // Mehrfachaufrufe zusammenfassen: Im Entwicklungsmodus ruft React (StrictMode)
  // die Start-Effekte doppelt auf, und zwei gleichzeitige Abgleiche würden auch
  // die einmalige Übernahme doppelt starten.
  tenantAdmin = isTenantAdmin
  if (inFlight && inFlight.userId === userId) return inFlight.promise
  const promise = runSync(userId)
  inFlight = { userId, promise }
  try {
    await promise
  } finally {
    inFlight = null
  }
}

let inFlight: { userId: string; promise: Promise<void> } | null = null

async function runSync(userId: string): Promise<void> {
  setUiSettingsUser(userId)

  let settings: Record<string, ResolvedUiSetting>
  try {
    const { data } = await api.get<{ settings: Record<string, ResolvedUiSetting> }>(
      '/users/me/ui-settings',
    )
    settings = data.settings ?? {}
  } catch {
    return
  }

  // Server gewinnt: gespiegelt wird der aufgelöste Wert (eigener, sonst Standard).
  // Der Mandanten-Standard wandert zusätzlich in einen eigenen Eintrag — daraus
  // speisen sich der „weicht ab"-Hinweis und das Zurücksetzen.
  const seen = new Set<string>()
  const seenDefaults = new Set<string>()
  for (const [key, entry] of Object.entries(settings)) {
    if (entry.tenantDefault != null) {
      seenDefaults.add(key)
      writeJson(tenantDefaultCacheKey(key), entry.tenantDefault)
    }
    const resolved = entry.value ?? entry.tenantDefault
    if (resolved == null) continue
    seen.add(key)
    lastSent.set(key, JSON.stringify(entry.value ?? null))
    writeJson(cacheKey(key), resolved)
  }
  dropStaleKeys(`${TENANT_DEFAULT_PREFIX}.${userId}.`, seenDefaults)

  // Was der Server nicht mehr kennt, ist auf einem anderen Gerät zurückgesetzt
  // worden und muss auch hier verschwinden — sonst bliebe das alte Layout ewig.
  dropStaleKeys(`${CACHE_PREFIX}.${userId}.`, seen)

  await migrateLegacySettings(settings)
}

// ─── Phase C: einmalige Übernahme der alten, lokalen Werte ───────────────────

/**
 * Ohne diesen Schritt verlören beim Rollout **alle** bestehenden Benutzer ihre
 * Einrichtung — genau der Fehler, den AP-23 behebt.
 *
 * Läuft genau einmal je Gerät. Die alten Schlüssel waren gerätegebunden, nicht
 * benutzergebunden; deshalb werden sie nach der Übernahme gelöscht, damit ein
 * zweiter Benutzer am selben Rechner nicht die Einrichtung des ersten erbt.
 * Bleibt eine Übernahme erfolglos (offline), wird der Merker nicht gesetzt und
 * nichts gelöscht — der nächste Start versucht es erneut.
 */
async function migrateLegacySettings(
  serverSettings: Record<string, ResolvedUiSetting>,
): Promise<void> {
  try {
    if (localStorage.getItem(MIGRATED_KEY) === '1') return
  } catch {
    return
  }

  const migrated: string[] = []

  for (const key of LEGACY_KEYS) {
    let raw: string | null = null
    try {
      raw = localStorage.getItem(key)
    } catch {
      return
    }
    if (raw == null) continue

    // Der Server gewinnt: Wer dort schon etwas hat, bekommt nichts überschrieben.
    if (serverSettings[key]?.value != null) {
      migrated.push(key)
      continue
    }

    const payload = parseLegacyValue(key, raw)
    if (payload === undefined) {
      migrated.push(key) // unlesbar — mitlöschen statt bei jedem Start neu versuchen
      continue
    }

    try {
      await api.put(`/users/me/ui-settings/${key}`, { value: payload })
    } catch {
      return // offline oder abgelehnt: Merker nicht setzen, beim nächsten Start erneut
    }

    lastSent.set(key, JSON.stringify(payload))
    writeJson(cacheKey(key), payload)

    // Die zuletzt getippten Filterwerte bleiben lokal, wandern aber unter den
    // benutzergebundenen Schlüssel, damit sie nicht mit dem alten Eintrag verschwinden.
    if (key.startsWith('filterbar_layout_')) {
      const lastValues = (JSON.parse(raw) as { lastValues?: unknown }).lastValues
      if (lastValues) saveFilterLastValues(key.replace('filterbar_layout_', ''), lastValues)
    }

    migrated.push(key)
  }

  try {
    localStorage.setItem(MIGRATED_KEY, '1')
    for (const key of migrated) localStorage.removeItem(key)
  } catch {
    // ignorieren
  }
}

/**
 * Übersetzt einen alten Wert in das, was der Server bekommt.
 * `undefined` heißt: unbrauchbar, nicht hochschieben.
 */
function parseLegacyValue(key: string, raw: string): unknown | undefined {
  if (key.startsWith('pagesize_')) {
    const n = parseInt(raw, 10)
    return Number.isFinite(n) && n > 0 ? n : undefined
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }
  if (parsed == null || typeof parsed !== 'object') return undefined

  // Filterleiste: nur die Feldauswahl gehört auf den Server, die getippten
  // Werte bleiben lokal.
  if (key.startsWith('filterbar_layout_')) {
    const fieldKeys = (parsed as { fieldKeys?: unknown }).fieldKeys
    return Array.isArray(fieldKeys) && fieldKeys.length > 0 ? { fieldKeys } : undefined
  }

  return parsed
}
